import type { TournamentMatch } from "@mixitone/models";
import type { MatchVoidReason } from "./TournamentEventTypes";
import { getMatchEntryIds } from "./tournamentMatchEligibility";
import { getMatchSlotId } from "./TournamentProjection";

export interface AutomaticVoidCommand {
  matchId: string;
  slot_id: string;
  round: number;
  match_index: number;
  division?: number;
  reason: MatchVoidReason;
  responsible_entry_ids: string[];
}

function isGroupStageMatch(match: TournamentMatch): boolean {
  const phase = match.format_data?.phase;
  if (phase === "playoff") return false;
  if (phase === "group") return true;
  return typeof match.format_data?.group === "string";
}

function isMatchResolved(match: TournamentMatch): boolean {
  return match.isResolved || match.status === "completed" || match.status === "voided";
}

/** Never auto-void a match that is already on court or has started. */
function isAssignedOrStarted(match: TournamentMatch): boolean {
  if (match.court_id) return true;
  if (match.isInProgress || match.status === "in_progress") return true;
  return false;
}

function matchInvolvesUncheckedEntry(match: TournamentMatch, uncheckedEntryIds: Set<string>): string[] {
  return getMatchEntryIds(match).filter((entryId) => uncheckedEntryIds.has(entryId));
}

/**
 * Pure planner: when every playable group-stage fixture is resolved, void the
 * remaining fixtures that still involve unchecked entries (no-show cutoff).
 *
 * Temporary assignment blocks (stand-down / occupied courts) are intentionally
 * ignored for the cutoff — only actual resolution status matters. Candidates
 * that already have a court or are in progress are skipped so live play is not
 * cancelled. Idempotent for already-voided matches.
 */
export function planAutomaticNoShowVoids(params: {
  matches: Iterable<TournamentMatch>;
  uncheckedEntryIds: Set<string>;
  /** When true, playoffs already exist and voids stay terminal (no re-open). */
  playoffsGenerated?: boolean;
}): AutomaticVoidCommand[] {
  const { uncheckedEntryIds, playoffsGenerated = false } = params;
  if (uncheckedEntryIds.size === 0) return [];

  const groupMatches = [...params.matches].filter(isGroupStageMatch);
  if (groupMatches.length === 0) return [];

  const involvingUnchecked: TournamentMatch[] = [];
  const playable: TournamentMatch[] = [];

  for (const match of groupMatches) {
    const responsible = matchInvolvesUncheckedEntry(match, uncheckedEntryIds);
    if (responsible.length > 0) {
      involvingUnchecked.push(match);
    } else {
      playable.push(match);
    }
  }

  if (involvingUnchecked.length === 0) return [];

  // Cutoff: every fixture that does not involve an unchecked entry must already
  // be resolved. Rest/occupancy do not count — those matches must still be played.
  const playableAllResolved = playable.every(isMatchResolved);
  if (!playableAllResolved) return [];

  // Once playoffs exist, still emit voids for any remaining unresolved no-show
  // fixtures (idempotent), but never reopen already-voided ones.
  void playoffsGenerated;

  const commands: AutomaticVoidCommand[] = [];
  for (const match of involvingUnchecked) {
    if (isMatchResolved(match)) continue;
    if (isAssignedOrStarted(match)) continue;
    if (!match.id) continue;

    const responsible = matchInvolvesUncheckedEntry(match, uncheckedEntryIds);
    if (responsible.length === 0) continue;

    const divisionRaw = match.format_data?.division;
    const division =
      typeof divisionRaw === "number" && Number.isFinite(divisionRaw)
        ? divisionRaw
        : typeof divisionRaw === "string"
          ? (() => {
              const n = Number.parseInt(divisionRaw, 10);
              return Number.isNaN(n) ? undefined : n;
            })()
          : undefined;

    commands.push({
      matchId: match.id,
      slot_id: getMatchSlotId(match),
      round: match.round ?? 0,
      match_index: match.match_index ?? 0,
      division,
      reason: "no_show",
      responsible_entry_ids: responsible,
    });
  }

  return commands;
}

/** Build the set of entry IDs that should be blocked from court assignment. */
export function collectUncheckedEntryIds(
  entries: Iterable<{ id?: string; isCheckedIn?: boolean }>,
): Set<string> {
  const unchecked = new Set<string>();
  for (const entry of entries) {
    if (!entry.id) continue;
    try {
      if (entry.isCheckedIn === false) {
        unchecked.add(entry.id);
      }
    } catch {
      // Relations not loaded — treat as checked to avoid false voids.
    }
  }
  return unchecked;
}
